import type { WireAttachedTool } from '@/api/wire';
import { toolName } from '@/app/technical';
import type { ItemContext } from './context';
import { dayWords } from './words';

/**
 * One line of "what agents may use here", said as a record says itself
 * (ADR-0155): what the tool is attached to, who holds it, what it has done
 * today and what would happen next. Read from what the gateway counted;
 * nothing is decided here.
 */

const ORIGIN = { core: 'a Google core tool', own: 'our own tool', third_party: 'a third party’s tool' } as const;
const plural = (n: number, one: string): string => `${n} ${n === 1 ? one : `${one}s`}`;

export function explainAttachedTool(t: WireAttachedTool, department: string, agentName: (id: string) => string = (id) => id): ItemContext {
  const name = toolName(t.toolId);
  const where = t.level === 'plan' && t.plan ? `the plan “${t.plan.name}”` : `all of ${department}`;
  const agents = t.agents.map(agentName);
  const spent = t.ceilingPerDay !== null && t.usedToday >= t.ceilingPerDay;
  const refused = !t.admitted && t.origin === 'third_party';
  const tip = refused
    ? 'Every call to it is refused until a superadmin admits it, whatever is attached here. Ask one if the work needs it.'
    : spent
      ? 'It has used its day’s allowance, so the gateway refuses it until the day turns (UTC). Nothing is broken; wait, or plan the calls across days.'
      : t.refusedToday > 0
        ? `${plural(t.refusedToday, 'call')} ${t.refusedToday === 1 ? 'was' : 'were'} refused today. If a grant is missing, the department’s lead asks a superadmin; if it was a ceiling or a rule, it is working as designed.`
        : t.until
          ? `Nothing is needed now. It ends on ${dayWords(t.until)}; ask for it to be renewed before then if the work continues.`
          : 'Nothing is needed. Its agents may use it within its ceiling until it is ended.';
  return {
    subject: { kind: 'tool', title: name },
    summary: `${name} is ${ORIGIN[t.origin]}, attached to ${where}${agents.length > 0 ? ` and held by ${agents.join(', ')}` : ''}.`,
    need: refused ? 'A superadmin’s admission.' : undefined,
    why: `Agents on this page may call it only as the gateway decides each call. Today ${plural(t.usedToday, 'call')}, ${t.refusedToday} refused.`,
    source: { label: t.level === 'plan' && t.plan ? `A grant for the plan “${t.plan.name}”` : `${department}’s standing assignments`, ...(t.level === 'plan' && t.plan ? { to: `/prospecting/plans/${t.plan.id}` } : {}) },
    involved: agents.map((n) => ({ name: n, kind: 'agent' as const })),
    facts: [
      ['Used today', t.ceilingPerDay !== null ? `${t.usedToday} of ${t.ceilingPerDay}` : `${t.usedToday}, with no daily ceiling in its manifest`],
      ['Until', t.until ? dayWords(t.until) : 'Until it is ended'],
    ],
    next: { tip, instead: 'The gateway counts every call, so what is shown is what happened, not an estimate.' },
  };
}
